import { useMemo } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import _ from "lodash"
import { Link } from "react-router-dom"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useExtension } from "hooks/extension/useExtension"
import { EXTENSION_NAME } from "extensions/em-service/constant"
import { useService } from "extensions/em-service/hooks/useService"

import { getIcon } from "utils/icon"

import { EmptyData } from "components/EmptyData"
import { Head } from "components/Head"
import { ImageCard } from "components/ImageCard"
import { ShopIcon } from "components/icons"
import { MotionDiv } from "components/motions"
import { VenueSelector } from "components/VenueSelector"
import { useVenueController } from "hooks/venue/useVenueController"

export const EmServiceMobilePage = () => {
  const { t } = useTranslation()
  const { isMultiVenueProject, venues } = useVenue()
  const { extensionConfig } = useExtension()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)

  const serviceConfig = useMemo(
    () =>
      extensionConfig?.[EXTENSION_NAME]?.options || {
        locations: [],
      },
    [extensionConfig]
  )
  const serviceLocations = _.get(serviceConfig, "locations")

  const { filterByVenueId, filteredServiceByVenue } = useService(
    serviceLocations,
    venueFilter
  )

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components?.[`EmServicePage`] || {}

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.service) || <ShopIcon />,
    [theme]
  )

  const {
    root,
    serviceImageCard,
    gridContainer,
    emptyDataContainer,
    venueSelector,
  } = styleOverrides

  return (
    <>
      <Head title={t("Service")} />
      <MotionDiv
        sx={root}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15 }}
      >
        {filteredServiceByVenue?.length > 0 ? (
          <Box sx={gridContainer}>
            {filteredServiceByVenue?.map((data) => {
              const { name, location, image, level_name } = data || {}
              const levelText = t("Level Text", {
                level: t(level_name),
              })
              return (
                <ImageCard
                  key={data?.id}
                  sx={serviceImageCard}
                  component={Link}
                  to={`/maps/place/${location?.id}`}
                  title={t(name)}
                  subheader={
                    <>
                      <Typography
                        className="location-text"
                        component="span"
                        variant="subtitle"
                      >
                        {levelText}
                      </Typography>
                    </>
                  }
                  image={image}
                  avatar={null}
                />
              )
            })}
          </Box>
        ) : (
          <Box sx={emptyDataContainer}>
            <EmptyData
              icon={emptyIcon}
              primary={t("Coming soon")}
              secondary={t("Stay tuned for the update")}
            />
          </Box>
        )}
        {isMultiVenueProject && (
          <Box sx={venueSelector}>
            <VenueSelector
              selectedVenue={venueFilter}
              venues={venues}
              onClickVenue={filterByVenueId}
              currentVenue={locationVenueId}
            />
          </Box>
        )}
      </MotionDiv>
    </>
  )
}
